/**
 * @fileoverview Couche d'accès aux données (Data Access Layer).
 * Gère exclusivement la connexion et les opérations CRUD avec l'API native IndexedDB.
 * Ce module est totalement agnostique de l'interface utilisateur (DOM).
 */

import { CONFIG } from './config.js';
import { normalizeIsbn } from './utils.js';

/** * Instance active de la base de données.
 * @type {IDBDatabase|null}
 */
let dbInstance = null;

/**
 * Initialise et ouvre la connexion à la base de données IndexedDB.
 * Si la base n'existe pas ou si la version a changé, crée le schéma et les index.
 * * @returns {Promise<IDBDatabase>} Une promesse résolue avec l'instance de la base de données.
 */
export function openDatabase() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(CONFIG.DB_NAME, CONFIG.DB_VERSION);

        // Déclenché lors de la création de la base ou du changement de CONFIG.DB_VERSION
        // Migration de indexDB (dès qu'il y a changement de CONFIG.DB_VERSION)
        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            const transaction = event.target.transaction;
            const oldVersion = event.oldVersion;

            // --- Création initiale (migration 0 → 1) ---
            if (oldVersion < 1) {
                const store = db.createObjectStore(CONFIG.DB_STORE_NAME, { keyPath: 'id', autoIncrement: true });
                store.createIndex('title', 'title', { unique: false });
                store.createIndex('series', 'series', { unique: false });
                store.createIndex('authors', 'authors', { unique: false });
                store.createIndex('tags', 'tags', { unique: false });
                store.createIndex('isbn', 'isbn', { unique: false });
            }

            // --- Migration 1 → 2 : index addedAt sur comics + store wishlist ---
            if (oldVersion < 2) {
                // -- Ajout de l'index addedAt sur le store comics --
                const store = transaction.objectStore(CONFIG.DB_STORE_NAME);

                // Guard : l'index peut déjà exister si la base a été créée avec DB_VERSION=1
                // alors que la branche `oldVersion < 2` était déjà codée (0 < 2 était vrai).
                if (!store.indexNames.contains('addedAt')) {
                    store.createIndex('addedAt', 'addedAt', { unique: false });

                    // Remplissage des enregistrements existants sans addedAt
                    const now = Date.now();
                    const cursorReq = store.openCursor();

                    cursorReq.onsuccess = (e) => {
                        const cursor = e.target.result;
                        if (!cursor) return;

                        if (!cursor.value.addedAt) {
                            cursor.update({ ...cursor.value, addedAt: now });
                        }

                        cursor.continue();
                    };
                }

                // -- Création du store wishlist (nouveau en v2) --
                // Schéma : { id, title, series, volume, authors, isbn, publisher, note, addedAt }
                // Tous les champs sont optionnels à l'exception de id (auto) et addedAt.
                const wish = db.createObjectStore(CONFIG.DB_WISH_STORE_NAME, { keyPath: 'id', autoIncrement: true });
                wish.createIndex('title',   'title',   { unique: false });
                wish.createIndex('series',  'series',  { unique: false });
                wish.createIndex('isbn',    'isbn',    { unique: false });
                wish.createIndex('addedAt', 'addedAt', { unique: false });
            }
        };

        // Déclenché quand la connexion est établie avec succès
        request.onsuccess = (event) => {
            dbInstance = event.target.result;
            resolve(dbInstance);
        };

        // Déclenché en cas de refus d'accès (ex: navigation privée stricte) ou erreur système
        request.onerror = (event) => {
            console.error("Erreur d'ouverture d'IndexedDB:", event.target.error);
            reject(event.target.error);
        };
    });
}

/**
 * Ajoute une nouvelle bande dessinée dans la base de données.
 * * @param {Object} comicData - Les données de la bande dessinée (titre, série, image, etc.).
 * @returns {Promise<number>} Une promesse résolue avec l'identifiant (ID) unique généré pour l'entrée.
 */
export function addComic(comicData) {
    return new Promise((resolve, reject) => {
        // Ouverture d'une transaction en lecture/écriture
        const store = dbInstance.transaction([CONFIG.DB_STORE_NAME], 'readwrite').objectStore(CONFIG.DB_STORE_NAME);

        // Enregistrement des data comic et de la date d'ajout
        const req = store.add({
            ...comicData,
            isbn: normalizeIsbn(comicData.isbn ?? ''),
            addedAt: Date.now()
        });

        req.onsuccess = (e) => resolve(e.target.result);
        req.onerror = (e) => reject(e.target.error);
    });
}

/**
 * Récupère l'intégralité des bandes dessinées stockées dans la base.
 * * @returns {Promise<Object[]>} Une promesse résolue avec un tableau contenant tous les objets BD.
 */
export function getAllComics() {
    return new Promise((resolve, reject) => {
        // Ouverture d'une transaction en lecture seule (optimisation des performances)
        const store = dbInstance.transaction([CONFIG.DB_STORE_NAME], 'readonly').objectStore(CONFIG.DB_STORE_NAME);
        const req = store.getAll();

        req.onsuccess = (e) => resolve(e.target.result);
        req.onerror = (e) => reject(e.target.error);
    });
}

/**
 * Supprime une bande dessinée spécifique de la base de données.
 * * @param {number} id - L'identifiant unique de la bande dessinée à supprimer.
 * @returns {Promise<void>} Une promesse résolue lorsque la suppression est terminée.
 */
export function deleteComic(id) {
    return new Promise((resolve, reject) => {
        const store = dbInstance.transaction([CONFIG.DB_STORE_NAME], 'readwrite').objectStore(CONFIG.DB_STORE_NAME);
        const req = store.delete(id);

        req.onsuccess = () => resolve();
        req.onerror = (e) => reject(e.target.error);
    });
}

/**
 * Met à jour une bande dessinée existante.
 * L'objet fourni doit impérativement contenir la propriété `id` de l'entrée à écraser.
 * * @param {Object} comicData - L'objet BD complet incluant les modifications et son `id`.
 * @returns {Promise<void>} Une promesse résolue lorsque la mise à jour est terminée.
 */
export function updateComic(comicData) {
    return new Promise((resolve, reject) => {
        const store = dbInstance.transaction([CONFIG.DB_STORE_NAME], 'readwrite').objectStore(CONFIG.DB_STORE_NAME);

        if (!comicData.addedAt) {
            comicData.addedAt = Date.now();
        }

        comicData.isbn = normalizeIsbn(comicData.isbn);

        // La méthode put() met à jour l'entrée si l'ID existe, sinon elle la crée
        const req = store.put(comicData);

        req.onsuccess = () => resolve();
        req.onerror = (e) => reject(e.target.error);
    });
}

/**
 * Enregistre un prêt sur une BD.
 * Écrit le champ `loan` sur l'enregistrement existant sans toucher au reste.
 *
 * @param {number} id - Identifiant de la BD prêtée.
 * @param {{ borrower: string, date: number }} loan - Détails du prêt (nom + timestamp).
 * @returns {Promise<void>}
 */
export function setLoan(id, loan) {
    return new Promise((resolve, reject) => {
        const store = dbInstance.transaction([CONFIG.DB_STORE_NAME], 'readwrite').objectStore(CONFIG.DB_STORE_NAME);
        const reqGet = store.get(id);

        reqGet.onsuccess = (e) => {
            const comic = e.target.result;
            if (!comic) {
                reject(new Error(`BD introuvable (id=${id})`));
                return;
            }

            comic.loan = { borrower: loan.borrower, date: loan.date };

            const reqPut = store.put(comic);
            reqPut.onsuccess = () => resolve();
            reqPut.onerror = (err) => reject(err.target.error);
        };

        reqGet.onerror = (e) => reject(e.target.error);
    });
}

/**
 * Marque une BD prêtée comme rendue (efface le champ `loan`).
 *
 * @param {number} id - Identifiant de la BD rendue.
 * @returns {Promise<void>}
 */
export function clearLoan(id) {
    return new Promise((resolve, reject) => {
        const store = dbInstance.transaction([CONFIG.DB_STORE_NAME], 'readwrite').objectStore(CONFIG.DB_STORE_NAME);
        const reqGet = store.get(id);

        reqGet.onsuccess = (e) => {
            const comic = e.target.result;
            if (!comic) {
                reject(new Error(`BD introuvable (id=${id})`));
                return;
            }

            comic.loan = null;

            const reqPut = store.put(comic);
            reqPut.onsuccess = () => resolve();
            reqPut.onerror = (err) => reject(err.target.error);
        };

        reqGet.onerror = (e) => reject(e.target.error);
    });
}

/**
 * Vérifie si un ISBN est déjà présent dans la collection.
 * Utilise l'index 'isbn' pour une recherche directe sans charger toute la base.
 *
 * @param {string} isbn - Le numéro ISBN à vérifier.
 * @returns {Promise<Object|null>} La BD existante si trouvée, null sinon.
 */
export function findComicByIsbn(isbn) {
    const normalizedIsbn = normalizeIsbn(isbn);

    return new Promise((resolve, reject) => {
        const store = dbInstance
            .transaction([CONFIG.DB_STORE_NAME], 'readonly')
            .objectStore(CONFIG.DB_STORE_NAME);

        const index = store.index('isbn');
        const request = index.get(normalizedIsbn);

        request.onsuccess = (e) => resolve(e.target.result ?? null);
        request.onerror = (e) => reject(e.target.error);
    });
}

// ============================================================
// --- CRUD WISHLIST ---
// ============================================================

/**
 * Ajoute un souhait d'achat dans la base de données.
 * Tous les champs sont optionnels ; `addedAt` est généré automatiquement.
 * La déduplication ISBN est à la charge de l'appelant via `findWishItemByIsbn`.
 *
 * @param {Object} wishData - Les données du souhait (title, series, isbn, note…).
 * @returns {Promise<number>} L'identifiant généré pour le nouvel enregistrement.
 */
export function addWishItem(wishData) {
    return new Promise((resolve, reject) => {
        const store = dbInstance
            .transaction([CONFIG.DB_WISH_STORE_NAME], 'readwrite')
            .objectStore(CONFIG.DB_WISH_STORE_NAME);

        const req = store.add({
            ...wishData,
            isbn: normalizeIsbn(wishData.isbn ?? ''),
            addedAt: Date.now()
        });

        req.onsuccess = (e) => resolve(e.target.result);
        req.onerror = (e) => reject(e.target.error);
    });
}

/**
 * Récupère l'intégralité des souhaits d'achat.
 *
 * @returns {Promise<Object[]>} Tableau de tous les souhaits stockés.
 */
export function getAllWishItems() {
    return new Promise((resolve, reject) => {
        const store = dbInstance
            .transaction([CONFIG.DB_WISH_STORE_NAME], 'readonly')
            .objectStore(CONFIG.DB_WISH_STORE_NAME);
        const req = store.getAll();

        req.onsuccess = (e) => resolve(e.target.result);
        req.onerror = (e) => reject(e.target.error);
    });
}

/**
 * Met à jour un souhait existant.
 * L'objet fourni doit impérativement contenir la propriété `id`.
 *
 * @param {Object} wishData - L'objet souhait complet incluant les modifications et son `id`.
 * @returns {Promise<void>}
 */
export function updateWishItem(wishData) {
    return new Promise((resolve, reject) => {
        const store = dbInstance
            .transaction([CONFIG.DB_WISH_STORE_NAME], 'readwrite')
            .objectStore(CONFIG.DB_WISH_STORE_NAME);

        if (!wishData.addedAt) wishData.addedAt = Date.now();
        wishData.isbn = normalizeIsbn(wishData.isbn ?? '');

        const req = store.put(wishData);

        req.onsuccess = () => resolve();
        req.onerror = (e) => reject(e.target.error);
    });
}

/**
 * Supprime un souhait d'achat par son identifiant.
 *
 * @param {number} id - L'identifiant du souhait à supprimer.
 * @returns {Promise<void>}
 */
export function deleteWishItem(id) {
    return new Promise((resolve, reject) => {
        const store = dbInstance
            .transaction([CONFIG.DB_WISH_STORE_NAME], 'readwrite')
            .objectStore(CONFIG.DB_WISH_STORE_NAME);
        const req = store.delete(id);

        req.onsuccess = () => resolve();
        req.onerror = (e) => reject(e.target.error);
    });
}

/**
 * Vérifie si un ISBN est déjà présent dans la liste de souhaits.
 * Utilise l'index 'isbn' pour une recherche directe sans charger toute la base.
 *
 * @param {string} isbn - Le numéro ISBN à vérifier.
 * @returns {Promise<Object|null>} Le souhait existant si trouvé, null sinon.
 */
export function findWishItemByIsbn(isbn) {
    const normalizedIsbn = normalizeIsbn(isbn);

    return new Promise((resolve, reject) => {
        const store = dbInstance
            .transaction([CONFIG.DB_WISH_STORE_NAME], 'readonly')
            .objectStore(CONFIG.DB_WISH_STORE_NAME);

        const index = store.index('isbn');
        const request = index.get(normalizedIsbn);

        request.onsuccess = (e) => resolve(e.target.result ?? null);
        request.onerror = (e) => reject(e.target.error);
    });
}

/**
 * Supprime tous les enregistrements du store comics.
 * Utilisé lors d'un import en mode remplacement.
 *
 * @returns {Promise<void>}
 */
export function clearAllComics() {
    return new Promise((resolve, reject) => {
        const store = dbInstance.transaction([CONFIG.DB_STORE_NAME], 'readwrite').objectStore(CONFIG.DB_STORE_NAME);
        const req = store.clear();

        req.onsuccess = () => resolve();
        req.onerror = (e) => reject(e.target.error);
    });
}

/**
 * Supprime tous les enregistrements du store wishlist.
 * Utilisé lors d'un import en mode remplacement.
 *
 * @returns {Promise<void>}
 */
export function clearAllWishItems() {
    return new Promise((resolve, reject) => {
        const store = dbInstance.transaction([CONFIG.DB_WISH_STORE_NAME], 'readwrite').objectStore(CONFIG.DB_WISH_STORE_NAME);
        const req = store.clear();

        req.onsuccess = () => resolve();
        req.onerror = (e) => reject(e.target.error);
    });
}
